import { CONFIG } from '../helper/config';
import { getRequestOptions, handleResponse, handleError } from './service-utilities';
import { GetCommentInfo, AddCommentInfo, EditCommentInfo, DeleteRequestInfo } from '../modal/narrative-board';
import { UserConfigModal } from '../modal/notification';


/**
 * Service for managing Narrative Board operations.
 *
 * Provides API calls to:
 * - Fetch and manage teams
 * - Add and delete narrative board cards
 * - Retrieve card history
 * - Manage comments (CRUD operations)
 * - Fetch team members associated with the board
 */
export const narrativeBoardService = {
  /** Fetches the teams for the given info, port number and auth token. */
  getTeams,

  /** Adds a card to the narrative board. */
  addCardToNarrativeBoard,

  /** Fetches narrative board cards for a tenant/team. */
  getNarrativeBoardCards,

  /** Fetches members of a narrative board team. */
  getNarrativeBoardTeamMembers,

  /** Deletes a narrative board card. */
  deleteNarrativeBoardCard,

  /** Fetches the history of a narrative board card. */
  getNarrativeBoardCardHistory,

  /** Fetches comments for a narrative board card. */
  getComments,

  /** Adds a new comment to a narrative board card. */
  addComment,

  /** Edits an existing comment on a narrative board card. */
  editComment,

  /** Deletes a comment from a narrative board card. */
  deleteComment,
};

/**
 * Fetches the teams for the given info, port number and auth token.
 * @param {object} info - The info object containing the necessary data for the request.
 * @param {string} port_no - The port number to be passed to the service.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getTeams(info: any, port_no: string, auth_token: string) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    CONFIG.API.REQUESTURL.GETTEAMS;
  let request_options: any = getRequestOptions(
    CONFIG.API.REQUESTMETHOD.POST, auth_token,
    JSON.stringify(info)
  );
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Adds a card to the narrative board and updates the store with the result.
 * @param {any} info - The request info object containing tenant and team identifiers.
 * @param {string} port_no - The port number to be passed to the service.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function addCardToNarrativeBoard(info: any, port_no: string, auth_token: string) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    CONFIG.API.REQUESTURL.ADDCARDTONARRATIVEBOARD;
  let request_options: any = getRequestOptions(
    CONFIG.API.REQUESTMETHOD.POST, auth_token,
    JSON.stringify(info)
  );
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches the narrative board cards for a specific tenant and team based on the provided
 * search string and page number.
 *
 * @param {any} info - The request info object containing tenant and team identifiers.
 * @param {number} insight_length - The number of cards per page to be fetched.
 * @param {string} search_string - The search query string for filtering results.
 * @param {string} auth_token - The authentication token to use for the request.
 * @param {number} [id] - The identifier of a specific card to be retrieved.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getNarrativeBoardCards(info: any, insight_length: number, search_string: string, auth_token: string, id?: any) {
  let url;
  console.log(id);
  if (id !== undefined) {
    url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.GETNARRATIVEBOARDCARDS + CONFIG.API.URLQUERYPARAMS.PAGE + insight_length + CONFIG.API.URLQUERYPARAMS.Id + id + CONFIG.API.URLQUERYPARAMS.QUERY + search_string;
  } else {
    url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.GETNARRATIVEBOARDCARDS + CONFIG.API.URLQUERYPARAMS.PAGE + insight_length + CONFIG.API.URLQUERYPARAMS.QUERY + search_string;
  }
  let request_options: any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST, auth_token, JSON.stringify(info))
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches team members for a given tenant and team and returns the server response.
 *
 * @param {string} tenant_id - The tenant identifier.
 * @param {string} team_id - The team identifier.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getNarrativeBoardTeamMembers(tenant_id: string, team_id: string, auth_token: string) {
  let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.GETTEAMMEMBERS + tenant_id + '/' + team_id;
  let request_options: any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST, auth_token,JSON.stringify({tenant_id:tenant_id}));
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Deletes a narrative board card with the given identifier and returns the server response.
 *
 * @param {number} insight_card_id - The identifier of the card to be deleted.
 * @param {DeleteRequestInfo} info - The request info containing tenant and team identifiers.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function deleteNarrativeBoardCard(insight_card_id: number, info: DeleteRequestInfo, auth_token: string) {
  let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.DELETENARRATIVEBOARDCARD + insight_card_id;
  let request_options: any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST, auth_token, JSON.stringify(info));
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches the history of a narrative board card with the given identifier and returns the server response.
 *
 * @param {number} insight_card_id - The identifier of the card whose history is to be fetched.
 * @param {number} history_length - The length of the insight history to be retrieved.
 * @param {UserConfigModal} user_context - The context of the user, containing necessary identifiers.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getNarrativeBoardCardHistory(
  insight_card_id: number,
  history_length: number,
  user_context: UserConfigModal,
  auth_token: string
) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    CONFIG.API.REQUESTURL.GETNARRATIVEBOARDCARDHISTORY +
    insight_card_id +
    CONFIG.API.URLQUERYPARAMS.PAGE +
    history_length;
    let request_options: any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST, auth_token, JSON.stringify(user_context));
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches the comments for a narrative board card with the given identifier and returns the server response.
 *
 * @param {GetCommentInfo} info - The request info containing the necessary identifiers.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getComments(info: GetCommentInfo, auth_token: string) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    CONFIG.API.REQUESTURL.GETCOMMENTS;
  let request_options: any = getRequestOptions(
    CONFIG.API.REQUESTMETHOD.POST, auth_token,
    JSON.stringify(info)
  );
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Adds a comment to a narrative board card with the given identifier and returns the server response.
 *
 * @param {AddCommentInfo} info - The request info containing the necessary identifiers and comment content.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function addComment(info: AddCommentInfo, auth_token: string) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    CONFIG.API.REQUESTURL.ADDCOMMENT;
  let request_options: any = getRequestOptions(
    CONFIG.API.REQUESTMETHOD.POST, auth_token,
    JSON.stringify(info)
  );
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Edits a comment on the narrative board and returns the server response.
 *
 * @param {EditCommentInfo} info - Information required to edit the comment, including the comment content and related identifiers.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function editComment(info: EditCommentInfo, auth_token: string) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    CONFIG.API.REQUESTURL.EDITCOMMENT;
  let request_options: any = getRequestOptions(
    CONFIG.API.REQUESTMETHOD.POST, auth_token,
    JSON.stringify(info)
  );
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Deletes a comment on the narrative board and returns the server response.
 *
 * @param {number} comment_id - The identifier of the comment being deleted.
 * @param {UserConfigModal} user_config_keys - Keys required for deleting a comment from the narrative board.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function deleteComment(comment_id: number, user_config_keys: UserConfigModal, auth_token: string) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    CONFIG.API.REQUESTURL.DELETECOMMENT +
    comment_id;
  let request_options: any = getRequestOptions(
    CONFIG.API.REQUESTMETHOD.POST, auth_token,
    JSON.stringify(user_config_keys)
  );
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}
